/**
 * APP版本详情页面
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Download, Edit, Power, PowerOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { AppVersion } from '@xiaozhi/shared-types';
import { UpdateType, PlatformType, VersionStatus } from '@xiaozhi/shared-types';
import { getVersionById, deleteVersion, updateVersion, getApkDownloadUrl } from '@/api/appVersion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import dayjs from 'dayjs';

export default function AppVersionDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState<AppVersion | null>(null);

  useEffect(() => {
    if (id) {
      loadVersion(Number.parseInt(id));
    }
  }, [id]);

  const loadVersion = async (versionId: number) => {
    try {
      setLoading(true);
      const data = await getVersionById(versionId);
      setVersion(data);
    } catch (error: any) {
      toast.error('加载版本信息失败', {
        description: error.message,
      });
      navigate('/app-version');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!version) return;
    
    try {
      await deleteVersion(version.id);
      toast.success('删除成功');
      navigate('/app-version');
    } catch (error: any) {
      toast.error(error.message || '删除失败');
    }
  };

  const handleToggleStatus = async () => {
    if (!version) return;
    
    try {
      const newStatus =
        version.status === VersionStatus.PUBLISHED ? VersionStatus.DISABLED : VersionStatus.PUBLISHED;
      
      const formData = new FormData();
      formData.append('status', newStatus.toString());
      
      await updateVersion(version.id, formData);
      toast.success(newStatus === VersionStatus.PUBLISHED ? '已启用' : '已禁用');
      loadVersion(version.id);
    } catch (error: any) {
      toast.error(error.message || '操作失败');
    }
  };

  const handleDownload = () => {
    if (!version) return;
    const filename = version.download_url.split('/').pop();
    if (filename) {
      const url = getApkDownloadUrl(filename);
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <LayoutWithSidebar title="版本详情">
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-8 px-4 max-w-4xl">
            <div className="text-center">加载中...</div>
          </div>
        </div>
      </LayoutWithSidebar>
    );
  }

  if (!version) {
    return (
      <LayoutWithSidebar title="版本详情">
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-8 px-4 max-w-4xl">
            <div className="text-center">版本不存在</div>
          </div>
        </div>
      </LayoutWithSidebar>
    );
  }

  // 解析更新说明
  const releaseNotes = typeof version.release_notes === 'string'
    ? JSON.parse(version.release_notes)
    : version.release_notes;

  // 更新类型Badge
  const getUpdateTypeBadge = (type: UpdateType) => {
    const variants = {
      [UpdateType.OPTIONAL]: { variant: 'default' as const, label: '可选更新' },
      [UpdateType.FORCE]: { variant: 'destructive' as const, label: '强制更新' },
      [UpdateType.SILENT]: { variant: 'secondary' as const, label: '静默更新' },
    };
    const config = variants[type];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <LayoutWithSidebar title="版本详情">
      <div className="flex-1 overflow-auto">
          <div className="container mx-auto py-8 px-4 max-w-4xl">
            {/* 头部导航 */}
            <div className="mb-6 flex items-center justify-between">
              <Button variant="ghost" onClick={() => navigate('/app-version')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                返回列表
              </Button>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/app-version/edit/${version.id}`)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  编辑
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleStatus}
                >
                  {version.status === VersionStatus.PUBLISHED ? (
                    <>
                      <PowerOff className="h-4 w-4 mr-2" />
                      禁用
                    </>
                  ) : (
                    <>
                      <Power className="h-4 w-4 mr-2" />
                      启用
                    </>
                  )}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                >
                  <Download className="h-4 w-4 mr-2" />
                  下载
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="h-4 w-4 mr-2" />
                      删除
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>确认删除</AlertDialogTitle>
                      <AlertDialogDescription>
                        确定要删除版本 {version.version_name} 吗？此操作不可恢复。
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>取消</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete}>删除</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            {/* 版本信息卡片 */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-2xl">版本 {version.version_name}</CardTitle>
                    <CardDescription>版本代码: {version.version_code}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {getUpdateTypeBadge(version.update_type as UpdateType)}
                    <Badge
                      variant={
                        version.status === VersionStatus.PUBLISHED
                          ? 'default'
                          : version.status === VersionStatus.DRAFT
                          ? 'secondary'
                          : 'outline'
                      }
                    >
                      {version.status === VersionStatus.PUBLISHED
                        ? '已发布'
                        : version.status === VersionStatus.DRAFT
                        ? '草稿'
                        : '已禁用'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* 基本信息 */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">基本信息</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">平台</div>
                      <div className="font-medium">
                        {version.platform === PlatformType.ANDROID ? 'Android' : 'iOS'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">渠道</div>
                      <div className="font-medium">{version.channel}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">文件大小</div>
                      <div className="font-medium">
                        {(version.file_size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">MD5校验值</div>
                      <div className="font-mono text-xs break-all">{version.file_md5}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">最低支持版本</div>
                      <div className="font-medium">{version.min_support_version}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">强制更新</div>
                      <div className="font-medium">
                        {version.force_update ? (
                          <Badge variant="destructive">是</Badge>
                        ) : (
                          <Badge variant="outline">否</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* 灰度发布 */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">灰度发布</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">灰度状态</div>
                      <div className="font-medium">
                        {version.gray_enabled ? (
                          <Badge variant="secondary">已启用</Badge>
                        ) : (
                          <Badge variant="outline">未启用</Badge>
                        )}
                      </div>
                    </div>
                    {version.gray_enabled && (
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">灰度比例</div>
                        <div className="font-medium">{version.gray_percentage}%</div>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* 更新说明 */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">更新说明</h3>
                  {releaseNotes && releaseNotes.length > 0 ? (
                    <ul className="list-disc list-inside space-y-2">
                      {releaseNotes.map((note: string, index: number) => (
                        <li key={index} className="text-gray-700 dark:text-gray-300">
                          {note}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500">暂无更新说明</p>
                  )}
                </div>

                <Separator />

                {/* 下载信息 */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">下载信息</h3>
                  <div className="space-y-2">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">下载地址</div>
                      <div className="font-mono text-xs break-all bg-gray-100 dark:bg-gray-800 p-2 rounded">
                        {version.download_url}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* 时间信息 */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">时间信息</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">创建时间</div>
                      <div className="font-medium">
                        {dayjs(version.create_time).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">更新时间</div>
                      <div className="font-medium">
                        {dayjs(version.update_time).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
      </div>
    </LayoutWithSidebar>
  );
}

